import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {Grid, MenuItem} from "@mui/material/";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {useTheftMisappropriationActions} from "../../../service/useTheftMisappropriationActions";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {MAIL_ADDRESS_REG_EXP} from "../../../component/Constants";


interface NewMessageModalProps {
    open: boolean,
    handleClose: Function,
    setListShouldUpdate: Function,
    idTheftMisappropriation: string,
    plate: string,
    tenant: string,
    dealer: string,
    eventTypeCode: number
}

const NewMessageModal = ({
                             open,
                             handleClose,
                             setListShouldUpdate,
                             idTheftMisappropriation,
                             plate,
                             tenant,
                             dealer,
                             eventTypeCode
                         }: NewMessageModalProps) => {

    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'theftMisappropriate'});
    const [templateValues, setTemplateValues] = useState<any[]>([]);
    const {sendMessage, getTemplates} = useTheftMisappropriationActions();
    const {dispatchGlobalData} = useContext(GlobalContext);

    const defaultValues = {template: "CUST", subject: "", recipients: [], message: ""};
    const validationSchema = Yup.object({
        subject: Yup.string().required(t('updateForm.requiredCheck')),
        recipients: Yup.array(Yup.string().matches(MAIL_ADDRESS_REG_EXP, {
            excludeEmptyString: true,
            message: t('createForm.emailNotValid')
        })).min(1, t('updateForm.requiredCheck')),
        message: Yup.string().required(t('updateForm.requiredCheck'))
    });
    const {handleSubmit, control, setValue, watch, reset} = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = async (values: any) => {
        let payload = {...values};
        // set placeholder subject with plate number
        payload.subject = (eventTypeCode === 0 ? t('listHeader.theftCard', {plate: plate}) : eventTypeCode === 1 ? t('listHeader.misapprCard', {plate: plate}) : "") + ": " + payload.subject;
        // send message
        let data = await sendMessage(payload, idTheftMisappropriation);
        if (data && data.status === "OK") {
            handleClose();
            reset(defaultValues);
            setListShouldUpdate(true);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.sentOk"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });

        }
    }

    useEffect(() => {
        getTemplates({tenant: tenant, dealer: dealer}).then(res => {
            setTemplateValues(res.result);
        });
    }, [])

    return (
        <Dialog scroll={"paper"} maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={open}
                onClose={() => {
                    return;
                }}>
            <form onSubmit={handleSubmit(data => {
                onSubmit({...data});
            }, errors => console.log(errors))} noValidate>
                <DialogTitle>
                    <Typography fontWeight={"bold"} color={"primary"}>
                        {t('message.newMessage')}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            handleClose();
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <Grid container item xs={12}>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={"template"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            label={t("message.template")}
                                            fullWidth
                                            variant="outlined"
                                            select
                                            value={field.value}
                                            onChange={(e) => {

                                                let val = e.target.value;
                                                if (val === "CUST") {
                                                    setValue("subject", "");
                                                    setValue("recipients", []);
                                                    setValue("message", "");
                                                } else {
                                                    let found = templateValues.find(t => t.templateCode === val);
                                                    if (found) {
                                                        setValue("subject", found.subject);
                                                        setValue("recipients", found.recipients.length > 0 ? found.recipients : []);
                                                        setValue("message", found.message);
                                                    }
                                                }
                                                field.onChange(e);
                                            }}
                                        >
                                            <MenuItem key={"CUST"} value={"CUST"}>
                                                {t("message.customEmail")}
                                            </MenuItem>
                                            {templateValues?.map((l: any) => (
                                                <MenuItem key={l.templateCode} value={l.templateCode}>
                                                    {l.templateName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}/>
                                <Controller
                                    control={control}
                                    name={"recipients"}
                                    render={({field, fieldState: {error}}) => (
                                        <Autocomplete
                                            multiple
                                            clearOnBlur={true}
                                            blurOnSelect={false}
                                            freeSolo
                                            id="recipients"
                                            options={[]}
                                            value={field.value}
                                            onChange={(e, newVal, reason) => field.onChange(newVal)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label={t('message.to')}
                                                    placeholder={t('chipList.placeholder')}
                                                    error={error !== undefined}
                                                    helperText={error && (error as unknown as Array<any>)[0] ? (error as unknown as Array<any>)[0].message : error && error.message ? error.message : ""}
                                                />
                                            )}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={"subject"}
                                    render={({field, fieldState: {error}}) => (
                                        <TextField
                                            fullWidth
                                            id="subject"
                                            label={t("message.subject")}
                                            variant={"outlined"}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={"message"}
                                    render={({field, fieldState: {error}}) => (
                                        <TextField
                                            fullWidth
                                            id="message"
                                            label={t("message.message")}
                                            multiline
                                            rows={6}
                                            variant={"outlined"}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"}
                            onClick={() => handleClose()}>{t('message.cancel')}
                    </Button><Button type="submit">{t('message.send')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default NewMessageModal;